import { ModuleRequestError, createModuleApi, readErrorCode } from '@wit-support/admin-client-core';
/**
 * Сетевой слой модуля «Комментарии»: лента комментариев (`/api/comments`) и
 * словарь тегов (`/api/tags`).
 *
 * `errorFormat` не задан: все запросы идут через `request()` ниже, который
 * разворачивает ошибку сам (`fetchRaw` + `readErrorCode` → `ModuleRequestError`
 * с машинным кодом и статусом), так что формат `unwrap` здесь не применяется.
 * Роуты отвечают кодом (`name_taken`, `not_found`, `invalid_id`, `forbidden`),
 * а не текстом: поверхности переводят его по своим картам, а 409 на
 * переименование тега разбирается по СТАТУСУ как ошибка поля.
 */
const api = createModuleApi();
export function setAnnotationsApiBaseUrl(url) {
    api.setBaseUrl(url);
}
async function request(path, init) {
    const r = await api.fetchRaw(path, init);
    if (!r.ok)
        throw new ModuleRequestError(await readErrorCode(r), r.status);
    return (await r.json());
}
// ─────────────────────────────── Лента ──────────────────────────────────────
/** Сколько комментариев тянуть за раз. Роут ограничен сверху сотней. */
export const COMMENTS_PAGE_SIZE = 50;
/**
 * Строка запроса ленты. Пустые фильтры НЕ уезжают на сервер вовсе: `q=` и
 * `authorId=` — это не «любой автор», а невалидные значения для
 * `CommentFeedQuery` (`min(1)`, `uuid()`), и роут ответил бы на них 400.
 */
export function commentFeedQueryString(query) {
    const p = new URLSearchParams();
    if (query.cursor)
        p.set('cursor', query.cursor);
    if (query.limit !== undefined)
        p.set('limit', String(query.limit));
    if (query.authorId)
        p.set('authorId', query.authorId);
    if (query.q)
        p.set('q', query.q);
    if (query.from)
        p.set('from', query.from);
    if (query.to)
        p.set('to', query.to);
    const s = p.toString();
    return s ? `?${s}` : '';
}
/** Страница ленты комментариев, новые сверху. */
export function listComments(query) {
    return request(`/api/comments${commentFeedQueryString(query)}`);
}
/**
 * Авторы для фильтра ленты — это просто список админов.
 *
 * Вызывается ТОЛЬКО при наличии права `admins.list` (владелец, супервайзер):
 * см. `CommentsPage`. Конверт `{admins}` разворачивается здесь, наружу уходит
 * минимум, который нужен выпадающему списку.
 */
export async function listCommentAuthors() {
    const r = await request('/api/admins');
    return r.admins.map((a) => ({ id: a.id, displayName: a.displayName }));
}
// ────────────────────────────── Словарь тегов ───────────────────────────────
/**
 * Сколько тегов тянуть в словарь — контрактный максимум `TagListQuery.limit`.
 * Серверный дефолт (50) для страницы управления мал: выдача отсортирована по
 * популярности, и редкие теги без явного предела остались бы за кадром.
 */
export const TAGS_PAGE_LIMIT = 200;
export function tagListQueryString(query) {
    const p = new URLSearchParams();
    if (query.q)
        p.set('q', query.q);
    if (query.limit !== undefined)
        p.set('limit', String(query.limit));
    const s = p.toString();
    return s ? `?${s}` : '';
}
/** Словарь тегов с числом диалогов у каждого. */
export function listTags(query = {}) {
    return request(`/api/tags${tagListQueryString(query)}`);
}
/** Переименование. 409 = «такое имя уже занято» (`ModuleRequestError.status`). */
export function renameTag(id, body) {
    return request(`/api/tags/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    });
}
/** Удаление тега; в ответе — со скольких диалогов он снят. */
export function deleteTag(id) {
    return request(`/api/tags/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
//# sourceMappingURL=api.js.map